import React, { useEffect, useState } from 'react'
import { Box, IconButton, Stack } from '@mui/material'
import { Delete } from '@mui/icons-material'
import { com } from '@eidu/form'
import actionLabel from '../../domain/permission/actionLabel'
import SelectOptionInput from './SelectOptionInput'
import SelectorEditor from './SelectorEditor'
import ActionTypeSelector, { ActionSelectorType } from './ActionTypeSelector'
import PathPermission from '../../pages/permission/path/PathPermission'
import { findEntityTypeByFieldId } from '../../util/EntityTypeExtensions'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import Permission = com.eidu.sharedlib.entity.permission.Permission
import GlobalPermission = com.eidu.sharedlib.entity.permission.GlobalPermission
import ALL_ACTIONS = com.eidu.sharedlib.entity.permission.ALL_ACTIONS
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import Action = com.eidu.sharedlib.entity.permission.Action
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.sharedlib.entity.permission.EntityPathPermission
import EntityPath = com.eidu.sharedlib.entity.path.EntityPath
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import NonEmptyList = com.quickbirdstudios.nonEmptyCollection.list.NonEmptyList

export type PermissionInputProps = {
  userSelector: EntitySelector
  entityTypes: readonly EntityType[]
  permission: Permission
  onChange: (updatedPermission: Permission) => void
  onDelete: () => void
}

function getDefaultActionSelectorType(permission: Permission): ActionSelectorType | undefined {
  if (permission instanceof EntitySelectorPermission) {
    return permission.targets === EntitySelector.All ? ActionSelectorType.All : ActionSelectorType.Entity
  } else if (permission instanceof EntityPathPermission) {
    return ActionSelectorType.Path
  } else {
    return undefined
  }
}

const PermissionInput: React.FC<PermissionInputProps> = ({
  userSelector,
  entityTypes,
  permission,
  onChange,
  onDelete,
}: PermissionInputProps) => {
  const [actionSelectorType, setActionSelectorType] = useState<ActionSelectorType | undefined>(
    getDefaultActionSelectorType(permission)
  )
  const [action, setAction] = useState<Action>(permission.action)

  // The state of an entity target can be undefined if the user hasn't entered all required information
  const [entitySelector, setEntitySelector] = useState<EntitySelector | EntityPath | undefined>(
    permission instanceof EntitySelectorPermission
      ? permission.targets
      : permission instanceof EntityPathPermission
        ? permission.path
        : undefined
  )

  const [userEntityTypeId, setUserEntityTypeId] = useState<EntityTypeId | undefined>()

  useEffect(() => {
    if (userSelector instanceof EntitySelector.FieldValueEquals) {
      const newEntityTypeId = findEntityTypeByFieldId(entityTypes, userSelector.fieldId)
      if (newEntityTypeId) setUserEntityTypeId(newEntityTypeId.id)
    } else if (userSelector instanceof EntitySelector.OfType) {
      setUserEntityTypeId(userSelector.typeId)
    } else {
      setUserEntityTypeId(undefined)
    }
  }, [userSelector])

  useEffect(() => {
    if (action instanceof GlobalAction) {
      setActionSelectorType(undefined)
      onChange(new GlobalPermission(action))
    } else if (action instanceof EntityAction && entitySelector instanceof EntitySelector) {
      setActionSelectorType(entitySelector === EntitySelector.All ? ActionSelectorType.All : ActionSelectorType.Entity)
      onChange(new EntitySelectorPermission(action, entitySelector))
    } else if (action instanceof EntityAction && entitySelector instanceof EntityPath) {
      setActionSelectorType(ActionSelectorType.Path)
      onChange(new EntityPathPermission(action, entitySelector))
    }
  }, [action, entitySelector])

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <SelectOptionInput
          availableOptions={Array.from(ALL_ACTIONS.asJsReadonlySetView()).map((actionValue) => ({
            [actionLabel(actionValue)]: actionValue,
          }))}
          selected={action}
          setSelected={(newAction) => {
            // ActionTypeSelector doesn't choose a default selector so we explicitly use EntitySelector.All for an EntityAction
            if (newAction instanceof EntityAction) {
              setEntitySelector(EntitySelector.All)
            }
            setAction(newAction)
          }}
        />
        {action instanceof EntityAction && (
          <ActionTypeSelector
            includePath={
              userSelector instanceof EntitySelector.OfType || userSelector instanceof EntitySelector.FieldValueEquals
            }
            value={actionSelectorType ?? ActionSelectorType.All}
            onChange={(newActionSelectorType) => {
              // SelectorEditor doesn't choose a default selector so we do it here, PathPermissionComponent does so we don't need to consider it
              if (newActionSelectorType === ActionSelectorType.Entity) {
                setEntitySelector(
                  new EntitySelector.Or(NonEmptyList.fromArray([new EntitySelector.OfType(entityTypes[0].id)]))
                )
              }
              setActionSelectorType(newActionSelectorType)
            }}
          />
        )}
        {actionSelectorType === ActionSelectorType.Entity && (
          <SelectorEditor
            selector={entitySelector as EntitySelector}
            onChange={setEntitySelector}
            entityTypes={entityTypes}
            selectorType="entity"
            enableOr
          />
        )}
        {actionSelectorType === ActionSelectorType.Path && (
          <PathPermission
            fromEntityTypeId={userEntityTypeId}
            entityTypes={entityTypes}
            preselectedPath={entitySelector as EntityPath}
            onChange={setEntitySelector}
          />
        )}
        <IconButton onClick={onDelete}>
          <Delete color="error" />
        </IconButton>
      </Stack>
    </Box>
  )
}

export type PermissionWithId = {
  id: string
  permission: Permission
}

export type PermissionsInputProps = {
  userSelector: EntitySelector
  entityTypes: readonly EntityType[]
  selected: readonly PermissionWithId[]
  onSelectedChange: (permissions: readonly PermissionWithId[]) => void
}

const PermissionsInput = ({ userSelector, entityTypes, selected, onSelectedChange }: PermissionsInputProps) => (
  <Stack direction="column" alignItems="flex-start" spacing={2} sx={{ p: 2 }}>
    {[...selected].map((permission, idx) => (
      <PermissionInput
        key={permission.id}
        userSelector={userSelector}
        entityTypes={entityTypes}
        permission={permission.permission}
        onChange={(newValue) =>
          onSelectedChange(
            selected.toSpliced(idx, 1, {
              id: selected[idx].id,
              permission: newValue,
            })
          )
        }
        onDelete={() => onSelectedChange(selected.toSpliced(idx, 1))}
      />
    ))}
  </Stack>
)

export default PermissionsInput
